import * as Sentry from '@sentry/react';
import { AxiosError } from 'axios';
import Pusher, { Channel } from 'pusher-js';
import React, {
  useState, useEffect, useLayoutEffect, useRef,
} from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  Route,
  Routes,
  useLocation,
  useNavigate,
  createBrowserRouter,
} from 'react-router-dom';
import { ToastContainer } from 'react-toastify';
import LoadingBar from 'react-top-loading-bar';

import AuthRouter from '@components/auth/AuthRouter';
import ClientRouter from '@components/client/ClientRouter';
import VerificationPage from '@components/client/verify/VerificationPage';
import ErrorRouter from '@components/error/ErrorRouter';
import GuestReferPage from '@components/guest/refer/GuestReferPage';
import GuestReferralPage from '@components/guest/referral/GuestReferralPage';
import appConfig from '@config/app';
import {
  getUserDetailsRequest,
} from '@reducers/auth/AuthAction';
import { updateMyNewDentistsCountDispatch } from '@reducers/clinic/ClinicAction';
import { updateMyClinicInvitesCountDispatch } from '@reducers/dentist/DentistAction';
import { updateUnreadLabOrderMessageCountDispatch, updateLabOrderListDispatch, updateLabOrderMessageStatusDispatch } from '@reducers/lab-order/LabOrderAction';
import {
  updateUnreadReferralMessageCountDispatch, referralMessageArrivedDispatch,
  updateReferralListDispatch, updateReferralMessageStatusDispatch,
} from '@reducers/referral/ReferralAction';
// import { getUnreadNotificationsRequest } from '@reducers/shared/SharedAction';
import { handleError } from '@services/ErrorHandler';
import MessagingService from '@services/MessagingService';
import CustomDocumentViewerComponent from '@shared/components/CustomDocumentViewer/CustomDocumentViewer';
import ConfirmModalComponent from '@shared/components/modal/confirm-modal/ConfirmModal';
import sanitizeReturnTo from '@shared/helpers/navigation';
import DashboardLayout from '@shared/layout/dashboard/DashboardLayout';
import LoadingBlock from '@shared/utils/LoadingBlock';
import { NotificationContainer } from '@shared/utils/Notification';
import RouteErrorBoundary from '@shared/utils/RouteErrorBoundary/RouteErrorBoundary';
import 'react-toastify/dist/ReactToastify.css';

const Wrapper = ({ children }: {
  children?: any
}) => {
  const location = useLocation();
  useLayoutEffect(() => {
    if (location.hash) return;
    document.documentElement.scrollTo(0, 0);
  }, [ location.pathname, location.hash ]);

  return children;
};

const AppRouter = () => {
  const dispatch = useDispatch<any>();
  const navigate = useNavigate();
  const location = useLocation();
  const notification = useRef<Pusher | undefined>(undefined);
  const channel = useRef<Channel | undefined>(undefined);

  const [ loading, setLoading ] = useState(true);
  const loadingBarRef = useRef<any>(null);

  // const isLoggedIn = useSelector(({ Auth }) => !!Auth.accessToken);
  const isLoggedIn = useSelector(({ Auth }) => !!Auth.user.id);
  const user = useSelector(({ Auth }) => Auth.user);
  // const accessToken = useSelector(({ Auth }) => Auth.accessToken);
  const accessToken = useSelector(({ Auth }) => Auth.user.id);
  const params = new URLSearchParams(location.search);
  // const paramsAccessToken = params.get('token');
  const returnTo = params.get('returnTo');

  const subscribeNotifications = () => {
    if (!accessToken) return;
    if (!notification.current) {
      notification.current = MessagingService.initialize();
    }
  };

  useEffect(() => {
    loadingBarRef.current?.continuousStart();

    // Complete when the new route fully renders
    const handleFinish = () => loadingBarRef.current?.complete();

    // Give time for components to render before marking complete
    requestAnimationFrame(() => {
      if ('requestIdleCallback' in window) {
        window.requestIdleCallback(handleFinish);
      }
    });

    return () => handleFinish();
  }, [ location.pathname ]);

  useEffect(() => {
    if (appConfig.sentry.env !== 'local') {
      if (user?.id) {
        Sentry.setUser({
          id: user.id,
          email: user.email,
        });
        return;
      }

      // clear when logged out
      Sentry.setUser(null);
    }
  }, [ user ]);

  useEffect(() => {
    subscribeNotifications();

    if (notification.current) {
      // THIS WILL BE ENHANCED LATER ON
      channel.current = notification.current.subscribe(`private-client-notifications-${user.id}`);
      channel.current.bind('client_notifications_sent', ({ data }: any) => {
        const newData = data?.data;

        if (data?.notification_type === 'invites') {
          if (data?.data?.invites?.status === 'accepted') {
            dispatch(updateMyNewDentistsCountDispatch({ data }));
            return;
          }
          dispatch(updateMyClinicInvitesCountDispatch({ data }));
          return;
        }

        if (data?.notification_type === 'referral') {
          // for lab-orders
          if (newData?.type === 'lab-order') {
            dispatch(updateLabOrderListDispatch({ data }));
            dispatch(updateLabOrderMessageStatusDispatch({ data }));
            return;
          }
          // for referrals
          dispatch(updateReferralListDispatch({ data, userId: user.id }));
          dispatch(updateReferralMessageStatusDispatch({ data }));
          return;
        }

        if (newData?.referred_details?.type === 'lab-order') {
          dispatch(updateUnreadLabOrderMessageCountDispatch({ data: newData, userId: user.id }));
          return;
        }

        // Whether this referral's chat is the page open here (not, e.g., its edit form).
        const threadOpen = window.location.pathname === `/referral-messages/${newData?.referral_id}`;
        dispatch(updateUnreadReferralMessageCountDispatch({
          data: newData, userId: user.id, threadOpen,
        }));
        dispatch(referralMessageArrivedDispatch());
      });
    }

    return () => {
      if (notification.current) {
        notification.current?.unsubscribe(`private-client-notifications-${user.id}`);
        notification.current = undefined;
      }
      if (channel.current) {
        channel.current.unbind('client_notifications_sent');
      }
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [ accessToken ]);

  useEffect(() => {
    // Expose a function so SW messages can trigger navigation
    window.__routerNavigate = navigate;

    // If service worker already set a pending navigation
    if (window.__navigateTo) {
      navigate(window.__navigateTo, { replace: true });
      // clear after use
      window.__navigateTo = null;
    }

    let handler: ((event: MessageEvent) => void) | undefined;

    if ('serviceWorker' in navigator) {
      handler = (event: MessageEvent) => {
        if (event.data?.navigateTo) {
          navigate(event.data.navigateTo, { replace: true });
        }
      };

      navigator.serviceWorker.addEventListener('message', handler);
    }

    return () => {
      if (handler) {
        navigator.serviceWorker.removeEventListener('message', handler);
      }
    };
  }, [ navigate ]);

  // added this so that when the user changes/relogin
  // it will fetch the unread without refreshing
  // useEffect(() => {
  //   const initiate = async () => {
  //     if (!accessToken) return;
  //     try {
  //       await dispatch(getUnreadNotificationsRequest()).$promise;
  //     } catch (error) {
  //       handleError(error);
  //     }
  //   };

  //   initiate();
  //   // eslint-disable-next-line react-hooks/exhaustive-deps
  // }, [ accessToken ]);

  // component did mount
  useEffect(() => {
    const onMount = async () => {
      // for verification redirect, check the query params
      // if accessToken is valid and returnTo is existing, user will be
      // automatically logged in
      if (isLoggedIn && returnTo) {
        // dispatch(setVerificationAccessTokenDispatch(paramsAccessToken));
        try {
          await dispatch(getUserDetailsRequest()).$promise;
          // returnTo already carries a leading slash (it is a full path);
          // prefixing another would produce `//referral-messages/...`.
          navigate(returnTo.startsWith('/') ? returnTo : `/${returnTo}`);
        } catch (error) {
          handleError(error);
          navigate('/errors/500');
        }
      }

      if (isLoggedIn) {
        try {
          await dispatch(getUserDetailsRequest()).$promise;
        } catch (error) {
          const { response: errorResponse } = error as AxiosError;
          if (errorResponse) {
            if (errorResponse.status === 401) {
              // set the last page path if the token expires and the user refreshes the page
              localStorage.setItem('lastPage', sanitizeReturnTo(location.pathname));
              // dispatch(clearUserTokenRequest());
            }
            return;
          }
          handleError(error);
          navigate('/errors/500');
        }
      }
      setLoading(false);
    };

    onMount();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (loading) return <LoadingBlock withReforalLogo enabled />;

  return (
    <div className="h-100">
      <NotificationContainer />
      <ConfirmModalComponent />
      <CustomDocumentViewerComponent />
      <Wrapper>
        <LoadingBar color="#FFC436" ref={loadingBarRef} height={3} />
        <Routes>
          <Route
            path="/auth/*"
            element={<AuthRouter />}
          />
          {/*
            ENH-052: public no-login referral view. Declared as a sibling of
            /auth/* and BEFORE the /* catch-all so it stays outside ClientRouter,
            where the authenticated layout and guards live — a guest has no
            session and must not be bounced to login.
          */}
          <Route
            path="/referral/view/:token"
            element={<GuestReferralPage />}
          />
          {/* ENH-052: a clinic's public referral form, for a dentist without an account. */}
          <Route
            path="/refer/:slug"
            element={<GuestReferPage />}
          />
          <Route
            path="/*"
            element={<ClientRouter />}
          />
          <Route element={<DashboardLayout />}>
            <Route
              path="/verify"
              element={<VerificationPage />}
            />
          </Route>
          <Route
            path="/errors/*"
            element={<ErrorRouter />}
          />
        </Routes>
      </Wrapper>

      <ToastContainer
        position="bottom-right"
        theme="dark"
        autoClose={3000}
      />
    </div>
  );
};

export const router = createBrowserRouter([
  {
    path: '*',
    Component: AppRouter,
    ErrorBoundary: RouteErrorBoundary,
  },
]);

export default AppRouter;
